import React, { useState, useEffect, useRef, useMemo } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import dynamic from 'next/dynamic';
import NextNprogress from 'nextjs-progressbar';

import config from 'appConfig';
import { Provider as AuthProvider } from 'lib/auth';
import { AuthHooks } from 'lib/auth-hooks';
import { AnalyticsPageView } from 'lib/analytics';
import initMockServer from 'api/initMockServer';
import { CookieConsent } from 'lib/cookie-consent';
import { NotificationProvider } from 'lib/notification';
import { useSaveCouponCodeFromLinkForLaterUse } from 'lib/coupons';

import { domAnimation, LazyMotion } from 'framer-motion';

// import Utils
import { initFunctions } from '../utils';

// import Custom Components
import Header from '../components/common/header';
import Footer from 'components/common/Footer';
import MobileMenu from '../components/common/mobile-menu';
import useFontLoader from 'hooks/useFontLoader';
import {
  CartContextProvider,
  MaybeApplySavedCouponCodeToCart,
} from 'lib/cart';
import {
  CardPaymentGatewayProvider,
  loadCardPaymentGateway,
  PaymentContextProvider,
} from 'lib/payment';
import { ConfirmationContextProvider } from 'lib/confirm';
import { DeliverySlotsContextProvider } from 'lib/deliveryDateTimes';
import { YotpoScripts } from 'lib/react-yotpo';
import { FacebookPixelScript } from 'lib/fb-pixel';
// import { OneSignalScript } from 'lib/onesignal';
import { useSaveMailchimpCampaignId } from 'lib/mailchimp';

// import Utils
import {
  mobileMenu,
  preventProductDefault,
  removePreventProductDefault,
  stickyHeaderHandler,
} from '../utils';

import 'bootstrap-icons/font/bootstrap-icons.css';
import '../scripts/wdyr';
import '../styles/globals.css';
import '../styles/style.scss';
import GoogleTagManager from 'lib/gtm/GoogleTagManager';
import App from 'next/app';
import { AddressContextProvider } from 'lib/address';
import { LoveMessageContextProvider } from 'lib/love-message';
import { SWRConfig } from 'swr';
import { SWRDefaultConfig } from 'lib/swr-crud';
import { CheckoutContextProvider } from 'lib/checkout';
import { YBPerksContextProvider } from 'lib/yb-perks/YBPerksContextProvider';
import { BottomBanner } from 'components/common/v2';
import { SumoScript } from 'lib/sumo';
import {
  PopupContextProvider,
  PostAddToCartPopup,
  ReferralIncentivePopup,
  TimedPopup,
} from 'lib/popups';
import { isTruthy } from 'lib/core';

const InputPhoneNumberModal = dynamic(() =>
  import('lib/account/InputPhoneNumberModal'),
);

if (process.env.NEXT_PUBLIC_API_MOCK_SERVER === 'true')
  initMockServer();

const cardPaymentGatewayLoader = loadCardPaymentGateway(
  config.cardPaymentGatewayKey,
);

function MyApp({
  Component,
  pageProps,
  err,
  nonce: serverNonce,
  collections,
  categories,
  router,
  cateringEnabled,
  ybExpressEnabled,
}) {
  const [loginError, setLoginError] = useState(false);

  // load fonts
  useFontLoader();

  initFunctions();
  // store.dispatch(getAllProducts());

  useSaveCouponCodeFromLinkForLaterUse();

  useSaveMailchimpCampaignId();

  // useLogRouterEvents();

  useEffect(() => {
    mobileMenu();

    // set sticky header
    stickyHeaderHandler();
    window.addEventListener('scroll', stickyHeaderHandler);

    // prevent product thumb icons
    preventProductDefault();

    return () => {
      window.removeEventListener('scroll', stickyHeaderHandler);

      // remove listeners of prevent product
      removePreventProductDefault();
    };
  }, []);

  const collectionsRef = useRef(collections);
  const categoriesRef = useRef(categories);
  const cateringEnabledRef = useRef(cateringEnabled);
  const ybExpressEnabledRef = useRef(ybExpressEnabled);

  const nonce = useMemo(() => {
    if (serverNonce) return serverNonce;

    if (typeof window !== 'undefined') {
      return window.__NEXT_DATA__.nonce ?? pageProps.nonce;
    }
    return '';
  }, [pageProps, serverNonce]);

  return (
    <React.Fragment>
      {/* next/script must not be used in _document.js, therefore rendered here */}
      <Script
        nonce={nonce}
        src="https://ajax.googleapis.com/ajax/libs/jquery/3.5.1/jquery.min.js"
        strategy="beforeInteractive"
      />

      <Script
        nonce={nonce}
        id="mcjs"
        src="/api/statics/mailchimp.js"
        strategy="afterInteractive"
      />

      <YotpoScripts
        nonce={nonce}
        apiKey={config.productReviewSaasKey}
      />

      <FacebookPixelScript nonce={nonce} pixelId={config.fbPixelId} />

      {/* <OneSignalScript nonce={nonce} /> */}

      <GoogleTagManager trackPageViews withNoScript nonce={nonce} />

      <Script
        id="openai-ads-pixel"
        nonce={nonce}
        strategy="afterInteractive"
        dangerouslySetInnerHTML={{
          __html: `
            (function (w, d, s, u) {
              if (w.oaiq) return;
              var q = function () {
                q.q.push(arguments);
              };
              q.q = [];
              w.oaiq = q;
              var js = d.createElement(s);
              js.async = true;
              js.src = u;
              var f = d.getElementsByTagName(s)[0];
              f.parentNode.insertBefore(js, f);
            })(window, document, "script", "https://bzrcdn.openai.com/sdk/oaiq.min.js");

            var m = document.cookie.match(/(?:^|; )__oaiq_consent=([^;]*)/);
            var storedConsent = m ? decodeURIComponent(m[1]) : null;
            var granted = storedConsent === "true";

            oaiq("consent", granted);

            oaiq("init", {
              pixelId: "${config.openaiAdsPixelId}",
            });
          `,
        }}
      />

      <AnalyticsPageView>
        <SWRConfig value={SWRDefaultConfig}>
          <CardPaymentGatewayProvider
            loader={cardPaymentGatewayLoader}
          >
            <AuthProvider session={pageProps.session}>
              <AuthHooks />
              <SumoScript nonce={nonce} />
              <CheckoutContextProvider>
                <PaymentContextProvider>
                  <CartContextProvider>
                    <ConfirmationContextProvider>
                      <DeliverySlotsContextProvider>
                        <AddressContextProvider>
                          <LoveMessageContextProvider>
                            <YBPerksContextProvider>
                              <PopupContextProvider>
                                <>
                                  {/* should be handled in _app.js instead of _document.js */}
                                  {/* reason: https://github.com/vercel/next.js/blob/master/errors/no-document-viewport-meta.md, https://github.com/vercel/next.js/blob/master/errors/no-document-title.md */}
                                  <Head>
                                    <title>{config.appTagline}</title>
                                    <meta
                                      name="viewport"
                                      content="width=device-width, initial-scale=1.0, minimum-scale=1.0"
                                    />
                                  </Head>

                                  <div className="page-wrapper">
                                    <Header
                                      cateringEnabled={
                                        cateringEnabledRef.current
                                      }
                                      ybExpressEnabled={
                                        ybExpressEnabledRef.current
                                      }
                                    />
                                    <LazyMotion
                                      features={domAnimation}
                                    >
                                      <Component
                                        {...pageProps}
                                        loginError={loginError}
                                        setLoginError={setLoginError}
                                        err={err}
                                      />
                                    </LazyMotion>

                                    <BottomBanner />
                                    <Footer
                                      collections={
                                        collectionsRef.current
                                      }
                                      categories={
                                        categoriesRef.current
                                      }
                                      cateringEnabled={
                                        cateringEnabledRef.current
                                      }
                                      ybExpressEnabled={
                                        ybExpressEnabledRef.current
                                      }
                                    />

                                    <NotificationProvider />

                                    <MaybeApplySavedCouponCodeToCart />

                                    <NextNprogress
                                      color="#ffc231"
                                      startPosition={0.3}
                                      stopDelayMs={200}
                                      height={5}
                                      showOnShallow={false}
                                      key={router.route}
                                    />
                                  </div>

                                  <MobileMenu
                                    cateringEnabled={
                                      cateringEnabledRef.current
                                    }
                                    ybExpressEnabled={
                                      ybExpressEnabledRef.current
                                    }
                                  />

                                  <CookieConsent />
                                  <InputPhoneNumberModal />
                                  <TimedPopup />
                                  <PostAddToCartPopup />
                                  <ReferralIncentivePopup />
                                </>
                              </PopupContextProvider>
                            </YBPerksContextProvider>
                          </LoveMessageContextProvider>
                        </AddressContextProvider>
                      </DeliverySlotsContextProvider>
                    </ConfirmationContextProvider>
                  </CartContextProvider>
                </PaymentContextProvider>
              </CheckoutContextProvider>
            </AuthProvider>
          </CardPaymentGatewayProvider>
        </SWRConfig>
      </AnalyticsPageView>
    </React.Fragment>
  );
}

export default MyApp;

MyApp.getInitialProps = async ({ Component, ctx }) => {
  const appProps = await App.getInitialProps({ Component, ctx });

  const nonce = ctx.res?.getHeader('x-nonce') || '';

  if (appProps.pageProps) {
    appProps.pageProps.nonce = nonce;
  }

  const collections = [];

  const newLaunchesCollections =
    process.env.NEW_LAUNCHES_COLLECTION_ID;
  const hotSellerCollections = process.env.HOT_SELLERS_COLLECTION_ID;

  if (newLaunchesCollections) {
    collections.push(JSON.parse(newLaunchesCollections));
  }

  if (hotSellerCollections) {
    collections.push(JSON.parse(hotSellerCollections));
  }

  const cateringEnabled = isTruthy(process.env.CATERING_ENABLED);
  const ybExpressEnabled = isTruthy(process.env.YB_EXPRESS_ENABLED);

  const categories = process.env.CATEGORIES
    ? JSON.parse(process.env.CATEGORIES)
    : [];

  return {
    ...appProps,
    nonce,
    collections,
    categories,
    cateringEnabled,
    ybExpressEnabled,
  };
};
